import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchCached, resolveImageUrl } from '@/lib/api';
import { Menu, X } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import { prefetchPublicRoute } from '@/lib/publicPageData';

const NAV_LINKS_BASE = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/statement-of-faith', label: 'Statement of Faith' },
  { path: '/founder', label: 'Founder' },
  { path: '/activities', label: 'Activities' },
  { path: '/gallery', label: 'Gallery' },
  { path: '/blog', label: 'Blog' },
  { path: '/contact', label: 'Contact' },
];

const LOGO_FALLBACK = '/logo.png';

export default function Header() {
  const [settings, setSettings] = useState(null);
  const [donationsEnabled, setDonationsEnabled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetchCached('/content/site_settings').then(setSettings).catch(() => {});
    fetchCached('/donations/settings').then(data => setDonationsEnabled(data.has_active_methods === true)).catch(() => {});
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const logoUrl = settings?.logo_url ? resolveImageUrl(settings.logo_url) : LOGO_FALLBACK;
  const NAV_LINKS = donationsEnabled
    ? [...NAV_LINKS_BASE, { path: '/donate', label: 'Donate', special: true }]
    : NAV_LINKS_BASE;

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 border-b border-white/50 bg-white/90 text-[#0B1F4D] shadow-[0_10px_35px_rgba(11,31,77,0.08)] backdrop-blur-xl transition-all duration-300"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between gap-3 h-16 md:h-20">
          <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3" data-testid="header-logo-link">
            <BrandLogo
              src={logoUrl}
              alt="Million Stars Community Ministry"
              variant="header"
            />
            <span className="block max-w-[9.5rem] whitespace-normal break-words font-semibold text-[#0B1F4D] text-[11px] leading-tight sm:max-w-none sm:text-sm md:text-base" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {settings?.site_name || 'Million Stars Community Ministry'}
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onMouseEnter={() => prefetchPublicRoute(link.path)}
                onFocus={() => prefetchPublicRoute(link.path)}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-2 rounded-full text-[13px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/25 ${
                  link.special
                    ? 'bg-[#0B1F4D] text-white hover:bg-[#1E4FA1] px-5 shadow-[0_12px_28px_rgba(11,31,77,0.18)]'
                    : location.pathname === link.path
                      ? 'text-[#1E4FA1] bg-[#EAF4FF] shadow-[inset_0_0_0_1px_rgba(30,79,161,0.12)]'
                      : 'text-[#0B1F4D] hover:text-[#1E4FA1] hover:bg-[#F7FBFF]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#DDEBFA] bg-white text-[#0B1F4D] shadow-[0_8px_22px_rgba(11,31,77,0.08)] transition-colors hover:bg-[#EAF4FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/25"
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="lg:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-[#DDEBFA] bg-white/95 shadow-[0_18px_40px_rgba(11,31,77,0.12)] backdrop-blur-xl animate-fade-in" data-testid="mobile-nav">
          <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onMouseEnter={() => prefetchPublicRoute(link.path)}
                onFocus={() => prefetchPublicRoute(link.path)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex min-h-11 items-center rounded-[8px] px-4 py-2.5 text-sm font-semibold leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/25 ${
                  link.special
                    ? 'bg-[#0B1F4D] text-white'
                    : location.pathname === link.path
                      ? 'text-[#1E4FA1] bg-[#EAF4FF]'
                      : 'text-[#0B1F4D] hover:text-[#1E4FA1] hover:bg-[#F7FBFF]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
